import { badgeArtElement } from './badges.js';
import { ownedPrizeIds, prizeIds } from '../core/game/unlock-rules.js';

/**
 * The vehicles she can choose to colour.
 *
 * One per pup, so the first thing she does is recognise something rather than being handed a generic
 * truck. Choosing is half the fun at three - and it is a choice she can make entirely from pictures.
 *
 * Each one also names its own pup, so the dog she can see through the windscreen is the dog off the card
 * she pressed. Every vehicle used to share one placeholder pup, which quietly threw away the recognition
 * the picker exists to buy.
 *
 * The art is original, drawn to resemble each vehicle's proportions rather than traced from the show.
 * That keeps it safe to hold in the repository, and each one can be refined or replaced individually by
 * tracing over a screenshot later (see docs/ART_PIPELINE.md) as long as the ids survive.
 */

export interface VehicleChoice {
  readonly id: string;
  /** Parent-facing only. The picker is pictures, nothing else. */
  readonly label: string;
  /**
   * The pup's colour. This is how the show tells them apart and how she will too - colour first, shape
   * second, no reading involved.
   *
   * It is shown as a tag in the corner of the screen rather than painted on the vehicle: on the car it
   * was part of a drawing that is supposed to be entirely hers, and a patch she was not allowed to
   * colour.
   *
   * Now that the tag is the pup's real badge (`content/badges.ts`), this is the colour *off that badge* -
   * so the disc it falls back to, and the discs drifting over the Lookout, are the colour she is about to
   * see rather than a second opinion about it. Everest was purple here against a teal badge.
   */
  readonly badgeColour: string;
  /**
   * Who drives it: a file in the pack's `pups/` directory.
   *
   * Named separately rather than sliced off the front of the vehicle id, because the pup and the vehicle
   * are two pieces of art a parent can replace independently - and a mapping that lives in a string split
   * is a mapping nobody can see.
   */
  readonly pupId: string;
}

export const VEHICLES: readonly VehicleChoice[] = [
  { id: 'marshall-firetruck', label: "Marshall's fire truck", badgeColour: '#eb0000', pupId: 'marshall' },
  { id: 'chase-cruiser', label: "Chase's police cruiser", badgeColour: '#015bbb', pupId: 'chase' },
  { id: 'skye-copter', label: "Skye's helicopter", badgeColour: '#f2308d', pupId: 'skye' },
  { id: 'rubble-digger', label: "Rubble's digger", badgeColour: '#ffd500', pupId: 'rubble' },
  { id: 'rocky-recycler', label: "Rocky's recycling truck", badgeColour: '#46dd3e', pupId: 'rocky' },
  { id: 'zuma-hovercraft', label: "Zuma's hovercraft", badgeColour: '#fd741e', pupId: 'zuma' },
  { id: 'everest-snowcat', label: "Everest's snowcat", badgeColour: '#05ceac', pupId: 'everest' },
  { id: 'tracker-jeep', label: "Tracker's jeep", badgeColour: '#a7c66b', pupId: 'tracker' },
];

/**
 * The tag, as an element: the pup's own badge, on a coloured disc until it arrives.
 *
 * Synchronous on purpose. Every caller appends this into a card or a corner it is already building, and
 * the disc it hands back is a complete answer to "whose car is this?" - so the art landing later upgrades
 * the tag and a fetch that never lands costs nothing. (The paw is drawn inline rather than reusing the paw
 * glyph, because that glyph belongs to the back button and nothing else may wear it.)
 */
export function badgeElement(vehicle: VehicleChoice): HTMLElement {
  const host = document.createElement('div');
  host.className = 'pup-badge';
  host.style.background = vehicle.badgeColour;
  host.title = vehicle.label;
  host.setAttribute('aria-hidden', 'true');
  host.innerHTML =
    '<svg viewBox="0 0 100 100" class="pup-badge-paw" aria-hidden="true">' +
    '<ellipse cx="50" cy="64" rx="24" ry="20" fill="#fff"/>' +
    '<circle cx="24" cy="42" r="10" fill="#fff"/>' +
    '<circle cx="40" cy="28" r="10" fill="#fff"/>' +
    '<circle cx="60" cy="28" r="10" fill="#fff"/>' +
    '<circle cx="76" cy="42" r="10" fill="#fff"/>' +
    '</svg>';

  void badgeArtElement(vehicle.pupId).then((art) => {
    if (art === null) return;
    // The badge brings its own shape and its own colours, so the disc under it goes entirely - and the
    // inline background has to be removed rather than overridden, because it would win against the class.
    host.style.removeProperty('background');
    host.classList.add('is-art');
    host.replaceChildren(art);
  });

  return host;
}

/**
 * The ones she can colour today: the three that are hers from the first launch, plus every car she has
 * earned since.
 *
 * Three at the start rather than eight, because "which car shall we colour today" is one of the few real
 * choices she gets and a choice that *grows* is worth more than a choice that was always the same size.
 * Three is still a genuine pick on day one - one of them being Marshall's, which is `DEFAULT_VEHICLE_ID`.
 *
 * Which cars are prizes is not written down here. It comes from the prize table
 * (`src/core/game/unlock-rules.ts`), so there is exactly one place that decides, and a car nothing grants
 * is free - the safe direction for a mistake to fall.
 *
 * A locked car is **absent from the chooser, not greyed out in it**: nothing she can press may do nothing.
 * Where she sees it before she has it is the Collection shelf, as a silhouette. And this filter deliberately
 * has no say over a car she has already made: re-colouring and driving one out of the garage go through
 * `begin()` and the mission, neither of which asks this question, so a template leaving the chooser can
 * never strand a drawing she owns.
 */
export function availableVehicles(unlocks: readonly string[]): readonly VehicleChoice[] {
  const prizes = new Set(prizeIds('vehicle'));
  const earned = new Set(ownedPrizeIds('vehicle', unlocks));
  return VEHICLES.filter((vehicle) => !prizes.has(vehicle.id) || earned.has(vehicle.id));
}

/** What a brand-new car starts as if something goes wrong choosing. */
export const DEFAULT_VEHICLE_ID = 'marshall-firetruck';

/**
 * The generic pup, for when there is no car yet - a fresh save looking at the Lookout, or a template id
 * from a pack this build does not know. A dog she does not recognise is still better than an empty seat.
 */
export const DEFAULT_PUP_ID = 'placeholder-pup';

export function vehicleById(id: string): VehicleChoice | null {
  return VEHICLES.find((vehicle) => vehicle.id === id) ?? null;
}

/**
 * The pup who rides in a given vehicle.
 *
 * The whole point of the picker is that she chooses the pup she recognises, and that only pays off if the
 * dog behind the windscreen is the one off the card she pressed.
 */
export function pupIdForVehicle(vehicleId: string | null): string {
  if (vehicleId === null) return DEFAULT_PUP_ID;
  return vehicleById(vehicleId)?.pupId ?? DEFAULT_PUP_ID;
}
